import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { initSentry, Sentry } from "@/lib/sentry";
import { initMonitoring } from "@/lib/monitoring";
import { initAnalytics, trackPageView } from "../lib/analytics";
import faviconAsset from "@/assets/favicon.png.asset.json";
import { ldScript, siteGraph } from "@/lib/schema";
import { Navigation } from "@/components/site/Navigation";
import { MobileTabBar } from "@/components/site/MobileTabBar";
import { Footer } from "@/components/site/Footer";
import { MiniPlayer } from "@/components/site/MiniPlayer";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { InstallPrompt } from "@/components/site/InstallPrompt";
import { ServiceWorkerUpdatePrompt } from "@/components/site/ServiceWorkerUpdatePrompt";
import { registerAppServiceWorker } from "@/lib/pwa/register";
import { initNativeBridge } from "@/lib/native/bridge";
import { CookieConsent } from "@/components/site/CookieConsent";
import { ContinueThread } from "@/components/site/ContinueThread";
import { AIGuide } from "@/components/site/AIGuide";
import { KaCurrent } from "@/components/site/KaCurrent";
import { FocusToggle } from "@/components/site/FocusToggle";
import { SurgeFeedback } from "@/components/site/SurgeFeedback";
import { FieldAnalytics } from "@/components/site/FieldAnalytics";
import { PresenceMode } from "@/components/site/PresenceMode";
import { useFloatingChromeSuppressed } from "@/lib/chrome-policy";
import { AmbientAudio } from "@/components/site/AmbientAudio";
import { CommandPalette } from "@/components/site/CommandPalette";
import { useReveal } from "@/hooks/use-reveal";

import { PlayerProvider } from "@/lib/player/PlayerContext";
import { useRouterState } from "@tanstack/react-router";
import { trackMarketingPageView } from "@/lib/marketing/analytics";


function NotFoundComponent() {
  return (
    <main className="flex min-h-[70vh] items-center justify-center px-4">
      <div className="max-w-md text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">404 · The path is unmarked</p>
        <h1 className="mt-4 font-serif text-5xl text-gold-luminous">Return to the field.</h1>
        <p className="mt-4 text-sm text-muted-foreground">
          What you are looking for is not on this route. Return home, or enter the frequency through the archive.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/" className="rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian">Go home</Link>
          <Link to="/music" className="rounded-full border border-gold px-5 py-2.5 text-sm text-gold-luminous hover:bg-gold hover:text-obsidian">Enter the music</Link>
        </div>
      </div>
    </main>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
    Sentry.captureException(error, { tags: { boundary: "tanstack_root_error_component" } });
  }, [error]);

  return (
    <main className="flex min-h-[70vh] items-center justify-center px-4">
      <div className="max-w-md text-center">
        <h1 className="font-serif text-3xl text-gold-luminous">The transmission was interrupted.</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Something did not resolve. Try again — or return to the entrance.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian"
          >
            Try again
          </button>
          <a href="/" className="rounded-full border hairline px-5 py-2.5 text-sm">Go home</a>
        </div>
      </div>
    </main>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
      { title: "Buddha KA | Artist, Author & Founder of KAfrequency" },
      {
        name: "description",
        content:
          "Official website of Buddha KA — Chicago-born independent recording artist, author, and founder of KAfrequency. Music, books, daily activations, visual worlds, and consciousness systems.",
      },
      { name: "author", content: "Buddha KA" },
      { name: "theme-color", content: "#080808" },
      { name: "color-scheme", content: "dark" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
      { name: "apple-mobile-web-app-title", content: "KAfrequency" },
      { name: "application-name", content: "KAfrequency" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Buddha KA · KAfrequency" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    // Single sitewide entity graph: Buddha KA (Person), KAfrequency
    // (Organization), buddhaka.com (WebSite). Every route references these
    // @ids instead of redefining them — see src/lib/schema.ts.
    scripts: [ldScript(siteGraph())],

    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "manifest", href: "/manifest.webmanifest" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/favicon-192.png" },
      { rel: "icon", type: "image/png", sizes: "96x96", href: "/favicon-96.png" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400&family=Inter+Tight:wght@300;400;500;600;700&display=swap",
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => {
    initSentry();
    initMonitoring();
    initAnalytics();
    registerAppServiceWorker();
    void initNativeBridge();
  }, []);
  useEffect(() => {
    Sentry.setTag("route", pathname);
  }, [pathname]);
  useEffect(() => {
    trackPageView(pathname);
    trackMarketingPageView(pathname);
  }, [pathname]);
  useReveal();
  const chromeSuppressed = useFloatingChromeSuppressed();


  return (
    <QueryClientProvider client={queryClient}>
      <PlayerProvider>
        <KaCurrent />
        <div className="relative z-10 flex min-h-dvh flex-col">
          <PaymentTestModeBanner />
          <Navigation />
          <main className="flex-1 pb-[calc(6rem+env(safe-area-inset-bottom))] lg:pb-24">
            <Outlet />
          </main>
          <Footer />
          <MiniPlayer />
          <MobileTabBar />
          <CookieConsent />
          {chromeSuppressed ? null : (
            <>
              <ContinueThread />
              <InstallPrompt />
              <ServiceWorkerUpdatePrompt />
              <AIGuide />
              <PresenceMode />
              <FocusToggle />
              <SurgeFeedback />
              <AmbientAudio />
            </>
          )}
          <FieldAnalytics />
          <CommandPalette />


        </div>
      </PlayerProvider>
    </QueryClientProvider>

  );
}
